import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '~/components/core';
import { useAppSelector, useStyles } from '~/hooks';
import { appSettingsSelector } from '~/state/selectors';
import { FONT_SIZE_XS, FONT_SIZE_S } from '~/constants';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';
import { InvestorResult } from '~/api-services/ReportsService/types';
import { formatTime } from '~/lib/format';

type Props = {
    result: InvestorResult;
};

export default function ResultRow({ result }: Props) {
    const styles = useStyles(stylesGenerator);
    const { colorScheme } = useAppSelector(appSettingsSelector);

    return (
        <View
            style={[
                styles.positionGroupContainer,
                colorScheme === 'dark' ? styles.borderColorDark : styles.borderColorWhite,
            ]}
            key={'instrumentContainer' + result.symbol}
        >
            <View
                style={[
                    styles.positionItemSide,
                    colorScheme === 'dark' ? styles.borderColorDark : styles.borderColorWhite,
                ]}
            >
                <View style={[styles.rotateTextWrapper, styles.buySell]}>
                    <Text fontType="LATO_BLACK" style={result.side === 'buy' ? styles.buyText : styles.sellText}>
                        {result.side.toUpperCase()}
                    </Text>
                </View>
            </View>
            <View style={styles.instrumentName}>
                <View>
                    <Text fontType="LATO_BOLD" style={styles.instrumentNameText}>
                        {result.symbolNameTranslated}
                    </Text>
                </View>
            </View>
            <View style={styles.positionGroupPrice}>
                <View style={styles.openClosePriceContainer}>
                    <Text style={[styles.positionItemPriceText]}>position-open</Text>
                    <Text style={[styles.positionItemPriceText]}>: </Text>
                    <Text style={[styles.positionItemPriceText]}>{result.openPriceFormatted}</Text>
                </View>
                <View style={styles.openClosePriceContainer}>
                    <Text style={[styles.positionItemPriceText]}>position-close</Text>
                    <Text style={[styles.positionItemPriceText]}>: </Text>
                    <Text style={[styles.positionItemPriceText]}>{result.closePriceFormatted}</Text>
                </View>
            </View>
            <View style={styles.positionGroupResult}>
                <View style={styles.timeContainer}>
                    <Text style={[styles.positionItemPriceText]}>{formatTime(result.timestamp)}</Text>
                </View>
                <Text
                    style={[
                        styles.positionItemPriceText,
                        result.resultSign >= 0 ? styles.positiveResult : styles.negativeResult,
                    ]}
                >
                    {result.resultFormatted.replace('-', '')}
                </Text>
            </View>
        </View>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        positionItemSide: {
            width: '5%',
            borderRightWidth: 1,
            justifyContent: 'center',
            alignItems: 'center',
        },
        instrumentName: { flexWrap: 'wrap', width: '35%', justifyContent: 'center', paddingLeft: scaleSize(10) },
        positionGroupPrice: { width: '35%', justifyContent: 'center', alignItems: 'center' },
        positionGroupResult: {
            justifyContent: 'center',
            alignItems: 'flex-end',
            width: '25%',
            paddingRight: scaleSize(10),
        },

        timeContainer: { paddingBottom: scaleSize(10) },
        openClosePriceContainer: { flexDirection: 'row', paddingVertical: scaleSize(5) },
        positionItemPriceText: { fontSize: FONT_SIZE_XS, color: theme.text_pendingOrder },
        buyText: {
            fontSize: FONT_SIZE_S,
            color: theme.text_upPrice,
        },
        positiveResult: { color: theme.text_upPrice },
        negativeResult: { color: theme.text_downPrice },
        sellText: {
            fontSize: FONT_SIZE_S,
            color: theme.text_downPrice,
        },
        buySell: { width: scaleSize(50), alignItems: 'center' },
        rotateTextWrapper: { transform: [{ rotate: '-90deg' }] },
        positionGroupContainer: {
            flexDirection: 'row',
            borderWidth: 1,
            width: '100%',
            borderRadius: scaleSize(5),
            marginTop: scaleSize(7),
            height: scaleSize(50),
            backgroundColor: theme.backgroundSecondary,
        },
        borderColorDark: { borderColor: theme.background },
        borderColorWhite: { borderColor: theme.border_2 },

        instrumentNameText: { fontSize: FONT_SIZE_S, letterSpacing: 0, color: theme.text_symbol },
    });
